import React, { useState, useCallback } from 'react';
import { Copy, Download, Clock, FileText, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import type { TranscriptionSegment } from '@/services/transcription';
import { TranscriptionAPI } from '@/services/transcription';
import { saveGeneratedTextFile } from '@/utils/downloadProxy';

interface TranscriptionResultProps {
  segments: TranscriptionSegment[];
  fullText: string;
  detectedLanguage: string;
  processingTime: number;
  className?: string;
}

export const TranscriptionResult: React.FC<TranscriptionResultProps> = ({
  segments,
  fullText,
  detectedLanguage,
  processingTime,
  className = '',
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [showSegments, setShowSegments] = useState(false);

  const handleCopyText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      showToast(
        'success',
        t('stt.results.copied.title', { defaultValue: 'Copied!' }),
        t('stt.results.copied.message', { defaultValue: 'Text copied to clipboard' })
      );
    } catch {
      showToast(
        'error',
        t('stt.results.copyFailed.title', { defaultValue: 'Copy Failed' }),
        t('stt.results.copyFailed.message', { defaultValue: 'Failed to copy text to clipboard' })
      );
    }
  }, [fullText, showToast, t]);

  /**
   * Save a generated transcript.
   *
   * One implementation for all three formats. Each button used to carry its
   * own copy of the blob + anchor-click dance, and every copy fired the
   * "Downloaded!" toast unconditionally — including when the save failed or
   * the user cancelled the dialog.
   */
  const saveTranscript = useCallback(
    async (content: string, filename: string, mimeType: string, formatLabel: string) => {
      try {
        const saved = await saveGeneratedTextFile(content, filename, mimeType);
        if (!saved) return; // user cancelled the native dialog
        showToast(
          'success',
          t('stt.results.downloaded.title', { defaultValue: 'Downloaded!' }),
          t('stt.results.downloaded.message', {
            // i18next placeholder, NOT a JS template literal: a literal is
            // baked in at build time, so every locale would show the English
            // sentence (guarded by
            // tests/test_frontend/test_i18n_defaultvalue_uses_placeholder_syntax_regression.py).
            defaultValue: 'Transcription saved as {{format}}',
            format: formatLabel,
          })
        );
      } catch (error) {
        showToast(
          'error',
          t('stt.results.downloadFailed.title', { defaultValue: 'Download Failed' }),
          error instanceof Error ? error.message : String(error)
        );
      }
    },
    [showToast, t]
  );

  const handleDownloadTxt = useCallback(() => {
    void saveTranscript(fullText, 'transcription.txt', 'text/plain', 'TXT');
  }, [fullText, saveTranscript]);

  const handleDownloadSRT = useCallback(() => {
    void saveTranscript(
      TranscriptionAPI.formatAsSRT(segments),
      'transcription.srt',
      'text/plain',
      'SRT'
    );
  }, [segments, saveTranscript]);

  const handleDownloadVTT = useCallback(() => {
    void saveTranscript(
      TranscriptionAPI.formatAsVTT(segments),
      'transcription.vtt',
      'text/vtt',
      'VTT'
    );
  }, [segments, saveTranscript]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  const getLanguageName = (code: string): string => {
    const languageNames: Record<string, string> = {
      en: 'English',
      es: 'Spanish',
      pt: 'Portuguese',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      ja: 'Japanese',
      ko: 'Korean',
      zh: 'Chinese',
      ru: 'Russian',
      ar: 'Arabic',
      hi: 'Hindi',
    };
    return languageNames[code] || code.toUpperCase();
  };

  return (
    <div className={`bg-black/30 backdrop-blur-xl rounded-lg border border-white/10 ${className}`}>
      {/* Header with stats */}
      <div className="p-4 border-b border-white/10 bg-green-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <h3 className="font-semibold text-green-300">
              {t('stt.results.title', { defaultValue: 'Transcription Complete' })}
            </h3>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap gap-4 mt-3 text-sm">
          <div className="flex items-center space-x-1 text-white/60">
            <Clock className="w-4 h-4" />
            <span>
              {t('stt.results.processingTime', {
                defaultValue: '{{time}}s',
                time: processingTime.toFixed(2),
              })}
            </span>
          </div>
          <div className="flex items-center space-x-1 text-white/60">
            <FileText className="w-4 h-4" />
            <span>
              {t('stt.results.segments', {
                defaultValue: '{{count}} segments',
                count: segments.length,
              })}
            </span>
          </div>
          <div className="flex items-center space-x-1 text-white/60">
            <span className="px-2 py-0.5 bg-blue-900/30 text-blue-300 rounded text-xs font-medium">
              {getLanguageName(detectedLanguage)}
            </span>
          </div>
        </div>
      </div>

      {/* Full text area */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-white/70">
            {t('stt.results.fullText', { defaultValue: 'Full Text' })}
          </h4>
          <button
            onClick={handleCopyText}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm text-white/60 hover:text-white/80 hover:bg-white/5 rounded transition-colors"
          >
            <Copy className="w-4 h-4" />
            <span>{t('common.copy', { defaultValue: 'Copy' })}</span>
          </button>
        </div>

        <div className="bg-black/20 rounded-lg p-4 max-h-64 overflow-y-auto">
          <p className="text-white/80 whitespace-pre-wrap leading-relaxed">{fullText}</p>
        </div>
      </div>

      {/* Segments toggle */}
      <div className="px-4 pb-2">
        <button
          onClick={() => setShowSegments(!showSegments)}
          className="flex items-center space-x-2 text-sm text-white/60 hover:text-white/80"
        >
          {showSegments ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span>
            {showSegments
              ? t('stt.results.hideSegments', { defaultValue: 'Hide segments' })
              : t('stt.results.showSegments', { defaultValue: 'Show segments with timestamps' })}
          </span>
        </button>
      </div>

      {/* Segments list */}
      {showSegments && (
        <div className="px-4 pb-4">
          <div className="bg-black/20 rounded-lg p-4 max-h-96 overflow-y-auto space-y-3">
            {segments.map((segment) => (
              <div
                key={`segment-${segment.start}-${segment.end}`}
                className="flex space-x-3 text-sm"
              >
                <span className="text-white/40 font-mono whitespace-nowrap">
                  {formatTime(segment.start)} - {formatTime(segment.end)}
                </span>
                <span className="text-white/80">{segment.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Download buttons */}
      <div className="p-4 border-t border-white/10 bg-black/20">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleDownloadTxt}
            className="flex items-center space-x-1 px-4 py-2 bg-black/30 border border-white/10 text-white/70 rounded-lg hover:bg-black/40 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            <span>{t('stt.results.downloadTxt', { defaultValue: 'Download TXT' })}</span>
          </button>
          <button
            onClick={handleDownloadSRT}
            className="flex items-center space-x-1 px-4 py-2 bg-black/30 border border-white/10 text-white/70 rounded-lg hover:bg-black/40 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            <span>{t('stt.results.downloadSrt', { defaultValue: 'Download SRT' })}</span>
          </button>
          <button
            onClick={handleDownloadVTT}
            className="flex items-center space-x-1 px-4 py-2 bg-black/30 border border-white/10 text-white/70 rounded-lg hover:bg-black/40 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            <span>{t('stt.results.downloadVtt', { defaultValue: 'Download VTT' })}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
